import {
    useCompletePasswordReset,
    useVerifyPasswordResetCode,
} from "@/api/authQueries"
import { Link, useSearchParams } from "react-router-dom"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { Button } from "@/components/ui/button"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { APP_ROUTES } from "@/common/urls"
import { CheckCheck, Loader } from "lucide-react"

const ResetPasswordSchema = z
    .object({
        newPassword: z
            .string()
            .min(6, { message: "Use at least six characters" }),
        confirmPassword: z
            .string()
            .min(2, { message: "Confirm password is required" }),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
        message: "Passwords don't match",
        path: ["confirmPassword"],
    })

export default function AuthResetPassword() {
    const [searchParams] = useSearchParams()
    const oobCode = searchParams.get("oobCode") || ""

    const { isLoading: isVerifying, error: verificationError } =
        useVerifyPasswordResetCode(oobCode)

    const {
        mutate: completePasswordReset,
        isPending,
        isSuccess,
    } = useCompletePasswordReset(oobCode)

    const form = useForm<z.infer<typeof ResetPasswordSchema>>({
        resolver: zodResolver(ResetPasswordSchema),
        defaultValues: {
            newPassword: "",
            confirmPassword: "",
        },
    })

    if (!oobCode) {
        return (
            <div className="grid place-items-center bg-center min-h-[calc(100dvh_-_theme(spacing.48))] w-full bg-no-repeat bg-auth-pattern pb-20 pt-8 px-4">
                <div className="bg-white shadow-lg border-input w-full max-w-[645px] rounded-lg py-8 sm:py-10 px-6 sm:px-16 md:px-32 gap-4 sm:gap-6 flex flex-col justify-center items-center">
                    <section className="flex flex-col gap-6 items-center text-center">
                        <h1 className="text-2xl text-app-danger font-bold">
                            Invalid Reset Link
                        </h1>
                        <p className="text-sm">
                            The reset link is missin, invalid or expired. Please
                            restart the process to request a new one.
                        </p>
                    </section>
                    <Link
                        className="text-app-defaultLight underline font-bold"
                        to={APP_ROUTES.AUTH.LOGIN}
                    >
                        Back to sign in
                    </Link>
                </div>
            </div>
        )
    }

    if (isVerifying) {
        return (
            <div className="grid place-items-center bg-center min-h-[calc(100dvh_-_theme(spacing.48))] w-full bg-no-repeat bg-auth-pattern pb-20 pt-8 px-4">
                <div className="bg-white shadow-lg border-input w-full max-w-[645px] rounded-lg py-8 sm:py-10 px-6 sm:px-16 md:px-32 gap-4 sm:gap-6 flex flex-col justify-center items-center">
                    <section className="flex flex-col gap-6 items-center text-center">
                        <h1 className="text-2xl text-app-danger font-bold">
                            Verifying Reset Link
                        </h1>
                        <p className="text-sm">
                            Please wait while we verify your reset link...
                        </p>
                        <Loader size={24} className="animate-spin" />
                    </section>
                </div>
            </div>
        )
    }

    if (verificationError) {
        return (
            <div className="grid place-items-center bg-center min-h-[calc(100dvh_-_theme(spacing.48))] w-full bg-no-repeat bg-auth-pattern pb-20 pt-8 px-4">
                <div className="bg-white shadow-lg border-input w-full max-w-[645px] rounded-lg py-8 sm:py-10 px-6 sm:px-16 md:px-32 gap-4 sm:gap-6 flex flex-col justify-center items-center">
                    <section className="flex flex-col gap-6 items-center text-center">
                        <h1 className="text-2xl text-app-danger font-bold">
                            Invalid Reset Link
                        </h1>
                        <p className="text-sm">
                            The reset link is missin, invalid or expired. Please
                            restart the process to request a new one.
                        </p>
                    </section>
                    <Link
                        className="text-app-defaultLight underline font-bold"
                        to={APP_ROUTES.AUTH.LOGIN}
                    >
                        Back to sign in
                    </Link>
                </div>
            </div>
        )
    }

    function onSubmit(values: z.infer<typeof ResetPasswordSchema>) {
        completePasswordReset(values.newPassword)
    }
    return (
        <div className="grid place-items-center bg-center min-h-[calc(100dvh_-_theme(spacing.48))] w-full bg-no-repeat bg-auth-pattern pb-20 pt-8 px-4">
            <div className="bg-white shadow-lg border-input w-full max-w-[645px] rounded-lg py-8 sm:py-10 px-6 sm:px-16 md:px-32 gap-4 sm:gap-6 flex flex-col justify-center items-center">
                <section className="flex items-center justify-center w-full">
                    <img
                        src="/CMHT_logo_large.svg"
                        alt="CMHT Logo"
                        className="max-w-full h-auto"
                    />
                </section>

                <section className="flex flex-col gap-6 items-center text-center">
                    <h1 className="text-2xl text-app-danger font-bold">
                        Reset Password
                    </h1>
                    <p className="text-sm">Enter your new password below</p>
                </section>
                <section className="flex flex-col w-full">
                    <Form {...form}>
                        <form
                            onSubmit={form.handleSubmit(onSubmit)}
                            className="flex flex-col gap-10"
                        >
                            <div className="gap-3 flex flex-col">
                                <FormField
                                    control={form.control}
                                    name="newPassword"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>New Password</FormLabel>
                                            <FormControl>
                                                <Input
                                                    className="rounded-lg border-black"
                                                    type="password"
                                                    placeholder="New Password"
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="confirmPassword"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>
                                                Confirm Password
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    className="rounded-lg border-black"
                                                    type="password"
                                                    placeholder="Confirm Password"
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <Button
                                disabled={
                                    isPending ||
                                    isSuccess ||
                                    Object.keys(form.formState.errors).length >
                                        0
                                }
                                size="lg"
                                type="submit"
                                className="flex justify-center items-center gap-4 disabled:cursor-not-allowed w-full"
                            >
                                <span>Reset Password</span>
                                {isPending && (
                                    <Loader
                                        size={24}
                                        className="animate-spin"
                                    />
                                )}
                                {isSuccess && (
                                    <CheckCheck
                                        size={24}
                                        className="animate-pulse"
                                    />
                                )}
                            </Button>
                        </form>
                    </Form>
                </section>
                <section className="text-sm">
                    <span>Back to</span>{" "}
                    <Link
                        className="text-app-defaultLight underline font-bold"
                        to={APP_ROUTES.AUTH.LOGIN}
                    >
                        sign in
                    </Link>
                </section>
            </div>
        </div>
    )
}
